import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCreateUnitCategory } from '../../../hooks/admin/useUnitCategories';
import type { UnitCategoryCreateRequest } from '../../../types/admin';

export default function UnitCategoryCreate() {
  const navigate = useNavigate();
  const createCategory = useCreateUnitCategory();

  const { register, handleSubmit, formState: { errors } } = useForm<UnitCategoryCreateRequest>();

  const onSubmit = async (data: UnitCategoryCreateRequest) => {
    try {
      await createCategory.mutateAsync(data);
      navigate('/admin/units');
    } catch (error) {
      console.error('Failed to create unit category:', error);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Create Unit Category</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Name Long *</label>
            <input
              type="text"
              {...register('nameLong', { required: 'Long name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Temperature"
            />
            {errors.nameLong && (
              <p className="text-red-600 text-sm mt-1">{errors.nameLong.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Name Short *</label>
            <input
              type="text"
              {...register('nameShort', { required: 'Short name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Temp"
            />
            {errors.nameShort && (
              <p className="text-red-600 text-sm mt-1">{errors.nameShort.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Base Unit Name *</label>
            <input
              type="text"
              {...register('baseUnitName', { required: 'Base unit name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Celsius"
            />
            {errors.baseUnitName && (
              <p className="text-red-600 text-sm mt-1">{errors.baseUnitName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">User Unit Precision *</label>
            <input
              type="number"
              {...register('userUnitPrecision', {
                required: 'Precision is required',
                valueAsNumber: true,
                min: { value: 0, message: 'Precision must be 0 or greater' },
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., 2"
            />
            {errors.userUnitPrecision && (
              <p className="text-red-600 text-sm mt-1">{errors.userUnitPrecision.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Optional description..."
            />
          </div>

          <div className="flex gap-4 justify-end">
            <button
              type="submit"
              disabled={createCategory.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {createCategory.isPending ? 'Creating...' : 'Create Unit Category'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/units')}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
